/**
 * Cart utility functions for managing cart state across the application
 */

/**
 * Get cart items from localStorage
 * @returns {Array} Array of cart items
 */
export const getCartItems = () => {
  const existingCart = localStorage.getItem('shoppingCart');
  if (existingCart) {
    try {
      return JSON.parse(existingCart);
    } catch (error) {
      console.error('Error parsing cart data:', error);
      return [];
    }
  }
  return [];
};

/**
 * Save cart items to localStorage
 * @param {Array} cartItems - Array of cart items to save
 */
export const saveCartItems = (cartItems) => {
  localStorage.setItem('shoppingCart', JSON.stringify(cartItems));
};

/**
 * Get total number of items in cart
 * @returns {number} Total quantity of all items in cart
 */
export const getCartItemCount = () => {
  const cartItems = getCartItems();
  return cartItems.reduce((total, item) => total + (item.Quantity || 0), 0);
};

/**
 * Trigger cart update event to notify all components
 * @param {Array} cartItems - Current cart items
 */
export const triggerCartUpdate = (cartItems) => {
  window.dispatchEvent(new CustomEvent('cartUpdated', { 
    detail: { cartItems: cartItems || getCartItems() } 
  }));
};

/**
 * Add item to cart with validation
 * @param {Object} product - Product to add
 * @param {number} quantity - Quantity to add
 * @param {Object} customizations - Customization options
 * @returns {boolean} Success status
 */
export const addToCart = (cartData = {}) => {
  const cartItems = getCartItems();
  
  // Always add as a new separate item (no merging)
  // const cartItem = {
  //   Id: product.Id,
  //   Name: product.Name,
  //   NameAr: product.NameAr,
  //   Price: product.Price,
  //   OfferPrice: customizations.finalPrice || product.OfferPrice || product.Price,
  //   Image: product.Image1,
  //   Quantity: quantity,
  //   MinOrders: product.MinOrders || 1,
  //   ColourId: customizations.selectedColorId,
  //   SizeId: customizations.selectedSizeId,
  //   ZipType: customizations.selectedZipType,
  //   SelectedCustomizations: customizations.selectedCustomizations || [],
  //   ColorName: customizations.colorName || '',
  //   ColorNameAr: customizations.colorNameAr || '',
  //   SizeName: customizations.sizeName || '',
  //   SizeNameAr: customizations.sizeNameAr || '',
  //   // Add unique timestamp to ensure each item is truly separate
    
  // };

  cartData.AddedAt = new Date().toISOString();
  
  cartItems.push(cartData);

  // Save to localStorage
  saveCartItems(cartItems);
  
  // Trigger update event
  triggerCartUpdate(cartItems);
  
  return true;
};

/**
 * Remove item from cart by index
 * @param {number} itemIndex - Index of item to remove
 */
export const removeFromCart = (itemIndex) => {
  const cartItems = getCartItems();
  if (itemIndex >= 0 && itemIndex < cartItems.length) {
    cartItems.splice(itemIndex, 1);
    saveCartItems(cartItems);
    triggerCartUpdate(cartItems);
  }
};

/**
 * Remove item from cart by unique identifier
 * @param {string} itemId - ID of item to remove
 * @param {Object} specifications - Item specifications (color, size, type)
 */
export const removeFromCartById = (itemId, specifications) => {
  const cartItems = getCartItems();
  const filteredItems = cartItems.filter(item => 
    !(item.Id === itemId && 
      item.ColourId === specifications.selectedColorId && 
      item.SizeId === specifications.selectedSizeId && 
      item.ZipType === specifications.selectedZipType)
  );
  
  saveCartItems(filteredItems);
  triggerCartUpdate(filteredItems);
};

/**
 * Update item quantity in cart by index
 * @param {number} itemIndex - Index of item to update
 * @param {number} newQuantity - New quantity
 */
export const updateCartItemQuantity = (itemIndex, newQuantity) => {
  const cartItems = getCartItems();
  if (itemIndex >= 0 && itemIndex < cartItems.length) {
    if (newQuantity <= 0) {
      cartItems.splice(itemIndex, 1);
    } else {
      cartItems[itemIndex].Quantity = newQuantity;
    }
    
    saveCartItems(cartItems);
    triggerCartUpdate(cartItems);
  }
};

/**
 * Update item quantity in cart by unique identifier
 * @param {string} itemId - ID of item to update
 * @param {Object} specifications - Item specifications
 * @param {number} newQuantity - New quantity
 */
export const updateCartItemQuantityById = (itemId, specifications, newQuantity) => {
  const cartItems = getCartItems();
  const itemIndex = cartItems.findIndex(item => 
    item.Id === itemId && 
    item.ColourId === specifications.selectedColorId && 
    item.SizeId === specifications.selectedSizeId && 
    item.ZipType === specifications.selectedZipType
  );

  if (itemIndex !== -1) {
    if (newQuantity <= 0) {
      cartItems.splice(itemIndex, 1);
    } else {
      cartItems[itemIndex].Quantity = newQuantity;
    }
    
    saveCartItems(cartItems);
    triggerCartUpdate(cartItems);
  }
};

/**
 * Clear entire cart
 */
export const clearCart = () => {
  saveCartItems([]);
  triggerCartUpdate([]);
};

/**
 * Global cart update function - can be called from anywhere
 * This function will refresh the cart count in the header
 */
export const refreshCartCount = () => {
  const cartItems = getCartItems();
  triggerCartUpdate(cartItems);
};

// Make cart functions available globally for debugging
if (typeof window !== 'undefined') {
  window.cartUtils = {
    getCartItems,
    getCartItemCount,
    addToCart,
    removeFromCart,
    removeFromCartById,
    updateCartItemQuantity,
    updateCartItemQuantityById,
    clearCart,
    refreshCartCount
  };
}
